/* React */
import { CSSProperties, useMemo } from 'react'

/* Redux */
import { useDispatch } from 'src/redux/hooks'

/* Next */
import Link from 'next/link'

/* Material */
import Container from '@mui/material/Container'
import Typo from '@mui/material/Typography'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import ListItemButton from '@mui/material/ListItemButton'
import { Theme } from '@mui/material/styles'
import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import PinterestIcon from '@mui/icons-material/Pinterest'
import YouTubeIcon from '@mui/icons-material/YouTube'

import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import parse from 'html-react-parser'

/* Types */
import {
  MenuPageBuilderFooterItem,
  MenuPageBuilderFooterColumn,
  menuPageBuilderFooterSpacingDefaults,
} from 'src/shared/dbTypes/menuPageBuilder'
import shopInfos from 'template/infos'
import shopConfig from 'template/config'
import { hasOptionalAnalytics } from 'src/functions/analyticsConfig'
import {
  getExternalLinkRel,
  isAnchorOnlyHref,
  isExternalHref,
} from 'src/functions/linkPolicy'
import {
  createLandingPageImageAiGeneratedMatcher,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({

    wrapper: {
      backgroundColor: 'var(--builder-footer-surface, #ffffff)',
      color: 'var(--builder-footer-text, inherit)',
      '& .MuiTypography-root, & .MuiListItemText-root, & .MuiListItemButton-root, & .MuiListItem-root': {
        color: 'inherit',
      },
    },

    grid: {
      display: 'grid',
      rowGap: theme.spacing(6),
      paddingTop: 'var(--builder-footer-infos-padding-top, 40px)',
      paddingBottom: 'var(--builder-footer-infos-padding-bottom, 40px)',
      paddingLeft: theme.spacing(6),
      paddingRight: theme.spacing(6),
      columnGap: theme.spacing(5),

      [theme.breakpoints.up(640)]: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      },
      [theme.breakpoints.up(1000)]: {
        gridTemplateColumns: 'repeat(var(--builder-footer-columns, 4), minmax(0, 1fr))',
      },
    },

    column: {
      minWidth: 0,
    },

    list: {},

    iconList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(.75),
      paddingTop: theme.spacing(1),
    },

    iconButton: {
      width: 34,
      minWidth: 34,
      height: 34,
      borderRadius: '50%',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      color: 'inherit',
      backgroundColor: 'rgba(255,255,255,.48)',
      border: '1px solid rgba(15, 23, 42, 0.12)',
      transition: 'background-color 160ms ease, transform 160ms ease',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,.78)',
        transform: 'translateY(-1px)',
      },
      '& svg': {
        fontSize: 19,
      },
    },

    itemImage: {
      maxHeight: 34,
      width: 'auto',
      maxWidth: 120,
      objectFit: 'contain',
      display: 'block',
    },
    itemImageWrap: {
      display: 'inline-block',
      lineHeight: 0,
      position: 'relative',
    },

    htmlText: {
      color: 'inherit',
      '& p': {
        margin: 0,
      },
      '& p:not(:last-child)': {
        marginBottom: theme.spacing(1.5),
      },
      '& a': {
        color: 'inherit',
      },
    },

    cookieSettingsButton: {
      appearance: 'none',
      border: 0,
      background: 'transparent',
      color: 'inherit',
      font: 'inherit',
      textAlign: 'left',
      textTransform: 'none',
      width: '100%',
    },
  })
)

const cookieSettingsHref = '#cookie-einstellungen'
const cookieSettingsItem: MenuPageBuilderFooterItem = {
  label: 'Cookie-Einstellungen',
  href: cookieSettingsHref,
}

const getNormalizedFooterText = (value?: string) => (value || '').toLocaleLowerCase('de-DE')

const isCookieSettingsItem = (item: MenuPageBuilderFooterItem) =>
  item.href === cookieSettingsHref || item.label === cookieSettingsItem.label

const isCustomerServiceColumn = (column: MenuPageBuilderFooterColumn) =>
  getNormalizedFooterText(column.title).includes('kundenservice')

const isCallbackFooterItem = (item: MenuPageBuilderFooterItem) => {
  const label = getNormalizedFooterText(item.label)
  const href = getNormalizedFooterText(item.href)

  return label.includes('rückruf')
    || label.includes('rueckruf')
    || href.includes('rueckruf')
    || href.includes('callback')
}

const isAboutPageItem = (item: MenuPageBuilderFooterItem) =>
  item.href === '/ueber-uns' || getNormalizedFooterText(item.label) === 'über uns'

const showAboutPageLink = (shopConfig as typeof shopConfig & {
  showAboutPageLink?: boolean
}).showAboutPageLink !== false

const withCookieSettingsItem = (columns: MenuPageBuilderFooterColumn[]) => {
  if (!hasOptionalAnalytics) return columns
  if (columns.some(column => column.items?.some(isCookieSettingsItem))) {
    return columns
  }

  const customerServiceIndex = columns.findIndex(isCustomerServiceColumn)
  const datenschutzIndex = columns.findIndex(column =>
    column.items?.some(item => item.href === '/datenschutzerklaerung' || item.label === 'Datenschutz')
  )
  const fallbackIndex = columns.findIndex(column => column.items?.some(item => !item.icon && !item.imageSrc))
  const targetIndex = [customerServiceIndex, datenschutzIndex, fallbackIndex, 0].find(index => index >= 0) || 0

  return columns.map((column, index) => {
    if (index !== targetIndex) return column

    const items = column.items || []
    const nextItems = [...items]
    const callbackIndex = items.findIndex(isCallbackFooterItem)

    if (callbackIndex >= 0) {
      nextItems.splice(callbackIndex, 1, cookieSettingsItem)
    } else {
      const privacyIndex = items.findIndex(item => item.href === '/datenschutzerklaerung' || item.label === 'Datenschutz')
      nextItems.splice(privacyIndex >= 0 ? privacyIndex + 1 : nextItems.length, 0, cookieSettingsItem)
    }

    return {
      ...column,
      items: nextItems,
    }
  })
}

const getFooterIcon = (icon?: string) => {
  switch (icon) {
    case 'instagram':
      return <InstagramIcon fontSize='small' />
    case 'pinterest':
      return <PinterestIcon fontSize='small' />
    case 'facebook':
      return <FacebookIcon fontSize='small' />
    case 'youtube':
      return <YouTubeIcon fontSize='small' />
    default:
      return null
  }
}

interface Props {
  backgroundColor?: string
  textColor?: string
  columns?: MenuPageBuilderFooterColumn[]
  columnsPerRow?: number
  paddingTop?: number
  paddingBottom?: number
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const InfosUndLinks: React.FC<Props> = ({
  backgroundColor,
  textColor,
  columns,
  columnsPerRow = 4,
  paddingTop,
  paddingBottom,
  landingPageImageAiGenerated,
}) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const isAiGeneratedLandingImage = useMemo(
    () => createLandingPageImageAiGeneratedMatcher(landingPageImageAiGenerated),
    [landingPageImageAiGenerated],
  )
  const wrapperStyle = {
    ['--builder-footer-surface' as string]: backgroundColor || undefined,
    ['--builder-footer-text' as string]: textColor || undefined,
    ['--builder-footer-columns' as string]: String(columnsPerRow || 4),
    ['--builder-footer-infos-padding-top' as string]: `${paddingTop ?? menuPageBuilderFooterSpacingDefaults.infosPaddingTop}px`,
    ['--builder-footer-infos-padding-bottom' as string]: `${paddingBottom ?? menuPageBuilderFooterSpacingDefaults.infosPaddingBottom}px`,
  } as CSSProperties

  const safeColumnsWithDefault: MenuPageBuilderFooterColumn[] = columns && columns.length > 0
    ? columns
    : [
        {
          id: 'footer-brand',
          title: shopInfos.name,
          items: [
            { label: 'Zahlung & Versand', href: '/zahlung-und-versand' },
            { label: 'Auslandsversandkosten', href: '/auslandsversandkosten' },
            { label: 'Impressum', href: '/impressum' },
            { label: 'AGB', href: '/agb' },
            { label: 'Widerrufsrecht', href: '/widerrufsbelehrung' },
            { label: 'Vertrag widerrufen', href: '/widerruf' },
            { label: 'Datenschutz', href: '/datenschutzerklaerung' },
          ],
        },
        {
          id: 'footer-contact',
          title: 'Kontakt',
          items: [
            { label: `Mail.: ${shopInfos.email}`, href: `mailto:${shopInfos.email}` },
            { label: 'Kontaktformular', href: '/kontakt' },
            { label: 'Mo - Fr. 09:00 bis 18:00 Uhr' },
          ],
        },
        {
          id: 'footer-payment',
          title: 'Zahlungsarten',
          items: [
            { label: 'PayPal' },
            { label: 'SEPA Lastschrift' },
            { label: 'Kreditkarte' },
          ],
        },
        {
          id: 'footer-shipping',
          title: 'Versandarten',
          items: [{ label: 'DHL für Pakete' }],
        },
      ]
  const safeColumns = showAboutPageLink
    ? safeColumnsWithDefault
    : safeColumnsWithDefault.map(column => ({
        ...column,
        items: column.items?.filter(item => !isAboutPageItem(item)),
      }))
  const renderedColumns = withCookieSettingsItem(safeColumns)

  return <div className={classes.wrapper} style={wrapperStyle}>
    <Container maxWidth='lg'>
      <div className={classes.grid}>
        {renderedColumns.map(column =>
          <div key={column.id || column.title} className={classes.column}>
            <Typo component='div' variant='h6' gutterBottom>
              {column.title}
            </Typo>

            {column.bodyHtml &&
              <div className={classes.htmlText}>
                {parse(column.bodyHtml)}
              </div>
            }

            {column.items && column.items.length > 0 &&
              <List className={[classes.list, column.items.some(item => item.icon) ? classes.iconList : ''].join(' ')}>
                {column.items.map((item, index) => {
                  const icon = getFooterIcon(item.icon)
                  const content = icon
                    ? icon
                    : item.imageSrc
                      ? <span className={classes.itemImageWrap}>
                          <img
                            src={item.imageSrc}
                            alt={item.imageAlt || item.label}
                            className={classes.itemImage}
                            loading='lazy'
                          />
                          <AiGeneratedBadge
                            aiGenerated={isAiGeneratedLandingImage(item.imageSrc)}
                            size='micro'
                            placement='top-left'
                          />
                        </span>
                      : <ListItemText primary={item.label} />

                  if (item.href) {
                    if (item.href === cookieSettingsHref) {
                      return <ListItemButton
                        key={`${column.id || column.title}-${index}`}
                        component='button'
                        type='button'
                        className={classes.cookieSettingsButton}
                        onClick={() => dispatch({ type: 'showCookieBanner' })}
                      >
                        {content}
                      </ListItemButton>
                    }

                    if (isAnchorOnlyHref(item.href)) {
                      return <ListItemButton
                        key={`${column.id || column.title}-${index}`}
                        component='a'
                        href={item.href}
                        target={isExternalHref(item.href) ? '_blank' : undefined}
                        rel={getExternalLinkRel(item.href, item.externalRel)}
                        className={icon ? classes.iconButton : undefined}
                        aria-label={icon ? item.label : undefined}
                        title={icon ? item.label : undefined}
                      >
                        {content}
                      </ListItemButton>
                    }

                    return <Link key={`${column.id || column.title}-${index}`} href={item.href} passHref legacyBehavior>
                      <ListItemButton
                        component='li'
                        className={icon ? classes.iconButton : undefined}
                        aria-label={icon ? item.label : undefined}
                        title={icon ? item.label : undefined}
                      >
                        {content}
                      </ListItemButton>
                    </Link>
                  }

                  return <ListItem
                    key={`${column.id || column.title}-${index}`}
                    className={icon ? classes.iconButton : undefined}
                    aria-label={icon ? item.label : undefined}
                    title={icon ? item.label : undefined}
                  >
                    {content}
                  </ListItem>
                })}
              </List>
            }
          </div>
        )}
      </div>
    </Container>
  </div>
}

export default InfosUndLinks
